import Pause from '@mui/icons-material/Pause'
import PlayArrow from '@mui/icons-material/PlayArrow'
import Stop from '@mui/icons-material/Stop'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useController } from 'react-hook-form'

import T from 'T'
import { formatMillisecondsToTime } from 'utils/time'

import { EXCEPTION_FORM_FIELD } from './constants'

import type { ExceptionFormValues } from './constants'
import type { Control } from 'react-hook-form'

const TICK_MS = 250

type ExceptionDelayFieldProps = {
  control: Control<ExceptionFormValues>
}

// Start / pause / reset stopwatch for the Delay type. Accumulates elapsed milliseconds into
// the `delayMs` form value (mirrors exception.delayTime, formatted with formatMillisecondsToTime).
const ExceptionDelayField: React.FC<ExceptionDelayFieldProps> = ({ control }) => {
  const {
    field: { value, onChange },
  } = useController({ control, name: EXCEPTION_FORM_FIELD.DELAY_MS })
  const [elapsed, setElapsed] = useState<number>(Number(value) || 0)
  const [running, setRunning] = useState(false)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const startedAtRef = useRef<number>(0)

  const stopTicking = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [])

  const handleStart = useCallback(() => {
    if (running) return
    startedAtRef.current = Date.now() - elapsed
    setRunning(true)
    intervalRef.current = setInterval(() => {
      const next = Date.now() - startedAtRef.current
      setElapsed(next)
      onChange(String(next))
    }, TICK_MS)
  }, [running, elapsed, onChange])

  const handlePause = useCallback(() => {
    stopTicking()
    setRunning(false)
  }, [stopTicking])

  const handleReset = useCallback(() => {
    stopTicking()
    setRunning(false)
    setElapsed(0)
    onChange('0')
  }, [stopTicking, onChange])

  useEffect(() => stopTicking, [stopTicking])

  return (
    <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
      <Typography sx={{ fontVariantNumeric: 'tabular-nums', minWidth: 84 }} variant="h6">
        {formatMillisecondsToTime(elapsed)}
      </Typography>
      {running ? (
        <IconButton aria-label={T.PAUSE} onClick={handlePause}>
          <Pause />
        </IconButton>
      ) : (
        <IconButton aria-label={T.START} onClick={handleStart}>
          <PlayArrow />
        </IconButton>
      )}
      <IconButton aria-label={T.RESET} onClick={handleReset}>
        <Stop />
      </IconButton>
    </Box>
  )
}

export default ExceptionDelayField
